/** Phase F (airport package): Taoyuan Airport, flights and HSR, plus the shared city services (toilets, rentals, the bank). */
export const enAirport = {
  airport: {
    mapNames: { airport: 'Taoyuan Airport' },
    zones: { ap_arrivals: 'Arrivals hall', ap_terminal: 'Terminal 2', ap_gates: 'Departure gates' },
    objects: {
      ap_mrt: 'Airport MRT (to Taipei)',
      ap_mrtTaipei: 'Airport MRT (to the airport)',
      ap_checkin: 'Check-in counter',
      ap_info: 'Information desk',
      ap_security: 'Security check',
      ap_gate: 'Gate C7 · Barcelona',
      ap_duty: 'Duty-free shop',
      ap_belt: 'Baggage belt',
      wc_public: 'Public toilet',
      rent_shop: 'Rent-a-ride',
      bank_counter: 'Lantern Bank',
      bank_atm: 'Cash machine',
    },
    npc: {},
    items: {} as Record<string, { name: string; desc: string }>,

    // --- Taoyuan Airport and the flight to Barcelona -------------------------------------------
    texts: {
      mrtToAirport: 'The purple Airport MRT line runs from Taipei Main Station to Taoyuan Airport in about 40 minutes.',
      mrtGo: '🚇 Ride to the airport',
      mrtBack: '🚇 Ride back to Taipei',
      mrtBackText: 'The Airport MRT takes you back to Taipei Main Station.',
      checkin: 'Good morning! Where are you flying today? Please have your passport ready.',
      checkinAsk: 'How do you ask for your ticket in English?',
      ask: ['One ticket to Barcelona, please.', 'Barcelona give ticket me.', 'I want go Barcelona now.'],
      wrong: 'Hmm, try again — think about the words “one ticket to… please”.',
      ticket: (price: number) => `✈️ Barcelona — ${price} coins (return)`,
      bought: 'Here is your boarding pass: gate C7, seat 23A by the window. Go through security, then follow the signs. Bon voyage!',
      poor: (price: number) => `A return flight to Barcelona costs ${price} coins. Save up at the bank and come back!`,
      haveTicket: 'You already have a boarding pass. Go through security and on to gate C7!',
      tooLate: (h: string) => `The last flight to Barcelona leaves at ${h} and has already gone today. Try again tomorrow morning.`,
      flown: 'You have already flown today. One big trip per day is plenty!',
      securityText: 'Security check. Put your bag on the belt. Which of these can you NOT take through in your hand luggage?',
      securityQ: ['A big bottle of water', 'A book', 'A sandwich'],
      securityOk: 'Right! Liquids over 100 ml stay out. Drink it or bin it — then come through.',
      securityWrong: 'Not that one. Think: what is liquid and bigger than a cup?',
      securityDone: 'You are through security. Gate C7 is on the right.',
      securityNoTicket: 'Boarding pass, please! Buy a ticket at the check-in counter first.',
      gateText: 'Gate C7. Flight BR 67 to Barcelona is boarding. About 14 hours in the air — bring a book!',
      board: '✈️ Board the plane',
      needSecurity: 'You need to go through security before boarding.',
      needTicket: 'No boarding pass? The check-in counter is on the left.',
      homeGate: 'Welcome back to Taiwan! Baggage belt 4, then the Airport MRT to Taipei.',
      info: 'Taoyuan Airport opened in 1979 and is the biggest airport in Taiwan. Two terminals, planes to over 100 cities — and a third terminal being built.',
      infoQ: 'Which of these is Taiwan’s biggest airport?',
      infoA: ['Taoyuan', 'Songshan', 'Kaohsiung'],
      infoOk: 'Yes! Taoyuan handles most international flights.',
      infoWrong: 'Not quite — it is the one you are standing in!',
      duty: 'Duty-free: pineapple cakes, tea, and tiny Taipei 101 models. A gift for someone back home?',
      dutyBuy: '🍍 Pineapple cakes (40 coins)',
      belt: 'Suitcases go round and round. Yours is the one with the sticker of a bunny.',
      flyTo: ['Taking off over the green plains of Taoyuan…', 'Clouds below, the sun above… you doze and wake, doze and wake…', 'Mountains! Then the blue Mediterranean… and there is Barcelona.'],
      flyHome: ['The plane climbs over the Mediterranean…', 'Dinner, a film, a long sleep…', 'Green mountains below: Taiwan! Welcome home.'],
      announce: (place: string) => `“Ladies and gentlemen, welcome to ${place}.”`,
      arrive: (place: string) => `Welcome to ${place}!`,
    },

    // --- High-speed rail (高鐵) from the town station -------------------------------------------
    hsr: {
      kaohsiung: 'Kaohsiung (HSR)',
      tainan: 'Tainan (HSR)',
      hintKaohsiung: 'the big harbour city in the south',
      hintTainan: 'the oldest city, temples and snacks',
      toSouth: ['The high-speed train glides out of the station at 300 km/h…', 'Rice fields and fish ponds flash past the window…', 'Tall buildings ahead — the south of Taiwan!'],
    },

    // --- Public toilets ----------------------------------------------------------------------------
    wc: { text: 'A clean public toilet. Remember to wash your hands!', use: '🚻 Use the toilet', wash: '🧼 Just wash your hands' },

    // --- Rent-a-ride ---------------------------------------------------------------------------------
    rent: {
      text: 'Rent a ride for the day! Faster than walking — and helmets are free.',
      bike: '🚲 Bike',
      kick: '🛴 Kick scooter',
      ebike: '🛵 E-bike (with helmet)',
      price: (n: number) => `${n} coins`,
      rented: (what: string) => `You rented a ${what}. Return it at any rental shop, or just walk inside somewhere.`,
      names: { bike: 'bike', kick: 'kick scooter', ebike: 'e-bike' },
      ret: '↩️ Return the ride',
      returned: 'Thanks! Ride returned.',
      poor: 'Not enough coins for that one.',
      indoor: 'You leave your ride at the door — no riding indoors!',
      helmet: 'Helmet on! In Taiwan everyone on an e-bike or scooter wears one.',
      rule: 'Ride slowly near people, and never on the pavement with the e-bike.',
    },

    // --- Lantern Bank -------------------------------------------------------------------------------
    bank: {
      title: '🏦 Lantern Bank',
      hello: (balance: number, coins: number) => `Welcome to Lantern Bank! Your account has **${balance}🪙**. In your pocket: **${coins}🪙**.`,
      atm: (balance: number, coins: number) => `Cash machine. Account: **${balance}🪙** · pocket: **${coins}🪙**.`,
      deposit: (n: number) => `⬆️ Deposit ${n}🪙`,
      depositAll: (n: number) => `⬆️ Deposit everything (${n}🪙)`,
      withdraw: (n: number) => `⬇️ Withdraw ${n}🪙`,
      withdrawAll: (n: number) => `⬇️ Withdraw everything (${n}🪙)`,
      statement: '📄 Statement',
      goal: '🎯 Savings goal',
      why: '❓ Why save in a bank?',
      whyText: 'Money in the bank is safe, and every morning the bank pays you **2 % interest** — a little thank-you for keeping it there. The more you save, the more it grows!',
      done: (balance: number) => `Done. Your account now has **${balance}🪙**.`,
      interest: (n: number, balance: number) => `🏦 The bank paid ${n}🪙 of interest. Balance: ${balance}🪙.`,
      empty: 'Your account is empty. Deposit a few coins to start earning interest!',
      stmtTitle: 'Last movements',
      stmtNone: 'No movements yet.',
      kinds: { in: 'Deposit', out: 'Withdrawal', interest: 'Interest', goal: 'Goal reached' },
      goalText: (goal: number, balance: number) => (goal ? `Your goal: **${goal}🪙**. You have ${balance}🪙 — ${Math.max(0, goal - balance)} to go.` : 'No goal yet. Pick one: reaching it earns you stars!'),
      goalSet: (n: number) => `🎯 Save ${n}🪙`,
      goalClear: 'Remove the goal',
      goalReached: (n: number) => `🎉 Goal reached: ${n}🪙 saved! +5⭐`,
      reward: 'Saving for a flight? Barcelona is 300 coins.',
      poor: 'You do not have that much in your pocket.',
      poorBank: 'There is not that much in the account.',
      payCard: (price: number, pocket: number, missing: number, bank: number) => `That is ${price}🪙 and you have ${pocket}🪙 in your pocket. Pay the other ${missing}🪙 with your bank card? (account: ${bank}🪙)`,
      payCardBtn: (n: number) => `💳 Pay ${n}🪙 from the account`,
      cannotPay: (price: number, pocket: number, bank: number) => `That is ${price}🪙. You have ${pocket}🪙 in your pocket and ${bank}🪙 in the bank: ${price - pocket - bank}🪙 short.`,
      wallet: '👛 Wallet',
      potName: 'Bank',
    },

    // --- Map panel ------------------------------------------------------------------------------------
    mapUi: {
      here: '📍 Here',
      taiwan: '🗺️ Taiwan',
      goHere: '🧭 Guide me',
      guiding: (what: string) => `🧭 Guiding you to ${what}`,
      pinCleared: 'You arrived.',
      kinds: { transport: 'Transport', rental: 'Rentals', toilet: 'Toilets', bank: 'Bank', food: 'Food', shop: 'Shops', sight: 'Sights', hotel: 'Hotel', info: 'Info', door: 'Doors' },
      door: (to: string) => `Door to ${to}`,
      how: {
        town: 'Home. Everything starts here: the station is at the east end of the main street.',
        taipei: 'Train from the station (60 coins). MRT to Ximending, Longshan, Liberty Square and Beitou.',
        hualien: 'Train from the station (90 coins). Tourist shuttle to Taroko and Qixingtan.',
        kaohsiung: 'High-speed rail from the station (150 coins).',
        tainan: 'High-speed rail from the station (130 coins).',
        airport: 'Airport MRT from Taipei Main Station.',
        barcelona: 'Flight from Taoyuan Airport (300 coins, return). Check in, security, gate C7.',
      },
      youAreHere: 'You are here',
      abroad: 'Abroad',
    },
  },
};
